import type { components } from '@connected/api-client/api.generated';
import type { QueryKey } from '@tanstack/react-query';

import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

import type { Device, DeviceKind } from '../types/hook-types';

import { createDevicePayloads } from './device-factory';

type QueryKeys = {
    listQueryKey: QueryKey;
};

type OptimisticContext = {
    listQueryKey: QueryKey;
    listPreviousData: components['schemas']['ListElectricalProjectDistributionBoardDeviceQueryResult'];
    previousDevice?: Device;
};

export type SearchParams = {
    electricalProjectVersionId: string;
    electricalProjectDistributionBoardId: string;
    deviceKind: DeviceKind;
};

type MutationVariables = {
    body?: object | null;
};

/**
 * Get the query keys for device list queries.
 */
export function getQueryKeys(search: SearchParams): QueryKeys {
    const listQueryOptions = elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
        electricalProjectVersionId: search.electricalProjectVersionId,
    });

    return {
        listQueryKey: listQueryOptions.queryKey as QueryKey,
    };
}

/**
 * Get the optimistic context including previous data for rollback.
 */
export function getOptimisticContext(search: SearchParams, deviceId?: string): OptimisticContext {
    const { listQueryKey } = getQueryKeys(search);

    const listPreviousData = queryClient.getQueryData(listQueryKey) as components['schemas']['ListElectricalProjectDistributionBoardDeviceQueryResult'];
    const previousDevice = deviceId
        ? listPreviousData?.Devices?.find(x => x.ElectricalProjectDistributionBoardDeviceId === deviceId)
        : undefined;

    return {
        listQueryKey,
        listPreviousData,
        previousDevice,
    };
}

/**
 * Update the device list with an add, remove, or update action.
 */
export function updateDeviceList(
    listPreviousData: components['schemas']['ListElectricalProjectDistributionBoardDeviceQueryResult'],
    device: Device,
    action: 'add' | 'remove' | 'update',
): components['schemas']['ListElectricalProjectDistributionBoardDeviceQueryResult'] {
    const devices = [...(listPreviousData?.Devices || [])];

    switch (action) {
        case 'add':
            return {
                ...listPreviousData,
                Count: (listPreviousData?.Count ?? 0) + 1,
                Devices: [...devices, device],
            };
        case 'remove':
            return {
                ...listPreviousData,
                Count: (listPreviousData?.Count ?? 0) - 1,
                Devices: devices.filter(d => d.ElectricalProjectDistributionBoardDeviceId !== device.ElectricalProjectDistributionBoardDeviceId),
            };
        case 'update':
            return {
                ...listPreviousData,
                Devices: devices.map(d => d.ElectricalProjectDistributionBoardDeviceId === device.ElectricalProjectDistributionBoardDeviceId ? { ...d, ...device } : d),
            };
    }
}

/**
 * Create optimistic device payloads for create/copy operations.
 */
export function createOptimisticDevice(
    electricalProjectVersionId: string,
    electricalProjectDistributionBoardId: string,
    deviceKind: DeviceKind,
    enableExtendedRiso: boolean,
    sourceDevice?: components['schemas']['EditElectricalProjectDistributionBoardDeviceQueryResult']['Device'],
    count: number = 1,
): components['schemas']['CreateElectricalProjectDistributionBoardDeviceCommand'][] {
    const { listPreviousData } = getOptimisticContext({ electricalProjectVersionId, electricalProjectDistributionBoardId, deviceKind });

    return createDevicePayloads({
        electricalProjectDistributionBoardId,
        deviceKind,
        enableExtendedRiso,
        existingDevices: listPreviousData?.Devices ?? [],
        sourceDevice,
    }, count);
}

/**
 * Handle optimistic update for device creation.
 */
export async function handleCreateOptimisticUpdate(
    search: SearchParams,
    variables: MutationVariables,
) {
    const body = toPascalCase(variables.body!) as components['schemas']['CreateElectricalProjectDistributionBoardDeviceCommand'];
    const { listQueryKey, listPreviousData } = getOptimisticContext(search, body.ElectricalProjectDistributionBoardDeviceId!);

    await queryClient.cancelQueries({ queryKey: listQueryKey });

    // Trust the incoming body values - Order, Identifier, RisoSign, RisoValue are already set by createOptimisticDevice
    const newEditData: components['schemas']['EditElectricalProjectDistributionBoardDeviceQueryResult'] = {
        Device: {
            ...body,
            ElectricalProjectDistributionBoardId: body.ElectricalProjectDistributionBoardId!,
            ElectricalProjectDistributionBoardDeviceId: body.ElectricalProjectDistributionBoardDeviceId!,
        },
    };

    const newListData = updateDeviceList(listPreviousData, newEditData.Device!, 'add');

    queryClient.setQueryData(listQueryKey, newListData);

    logger.debug({
        message: 'onMutate(handleCreateOptimisticUpdate)',
        path: '/api/v2/electrical/projects/distributionboards/devices/create',
        device: {
            previousData: listPreviousData,
            newData: newListData,
            queryKey: listQueryKey,
            body,
        },
    });

    return {
        listQueryKey,
        listPreviousData,
        newListData,
        newEditData,
    };
}

/**
 * Handle optimistic update for device deletion.
 */
export async function handleDeleteOptimisticUpdate(
    search: SearchParams,
    deviceId: string,
) {
    const { listQueryKey, listPreviousData, previousDevice } = getOptimisticContext(search, deviceId);

    await queryClient.cancelQueries({ queryKey: listQueryKey });

    const newListData = updateDeviceList(
        listPreviousData,
        { ElectricalProjectDistributionBoardDeviceId: deviceId } as Device,
        'remove',
    );

    queryClient.setQueryData(listQueryKey, newListData);

    logger.debug({
        message: 'onMutate(handleDeleteOptimisticUpdate)',
        path: '/api/v2/electrical/projects/distributionboards/devices/delete',
        device: {
            previousData: listPreviousData,
            previousDevice,
            newData: newListData,
            queryKey: listQueryKey,
            deviceId,
        },
    });

    return {
        listQueryKey,
        listPreviousData,
        previousDevice,
    };
}

/**
 * Handle optimistic update for device patching.
 */
export async function handlePatchOptimisticUpdate(
    search: SearchParams,
    deviceId: string,
    variables: MutationVariables,
) {
    const body = toPascalCase(variables.body!) as components['schemas']['PatchElectricalProjectDistributionBoardDeviceCommand'];
    const { listQueryKey, listPreviousData, previousDevice } = getOptimisticContext(search, deviceId);

    await queryClient.cancelQueries({ queryKey: listQueryKey });

    const newListData = updateDeviceList(
        listPreviousData,
        { ...previousDevice, ...body } as Device,
        'update',
    );

    queryClient.setQueryData(listQueryKey, newListData);

    logger.debug({
        message: 'onMutate(handlePatchOptimisticUpdate)',
        path: '/api/v2/electrical/projects/distributionboards/devices/patch',
        device: {
            previousData: listPreviousData,
            previousDevice,
            newData: newListData,
            queryKey: listQueryKey,
            body,
        },
    });

    return {
        listQueryKey,
        listPreviousData,
        previousDevice,
    };
}

/**
 * Rollback helper for optimistic updates on error.
 */
export function rollbackOptimisticUpdate(queryKey: QueryKey, previousData: unknown) {
    queryClient.setQueryData(queryKey, previousData);
}
